import React, { useState, useEffect } from 'react';
import Header from '../Includes/Header';
import Footer from '../Includes/Footer';
import "./FAQ.css";

function FAQ() {
    // State to keep track of which FAQ items are expanded
    const [expandedItems, setExpandedItems] = useState({});
    // State for search input
    const [searchQuery, setSearchQuery] = useState('');
    // State for selected category
    const [selectedCategory, setSelectedCategory] = useState('general');

    // Toggle FAQ item expansion
    const toggleExpand = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    // Clear filters function
    const clearFilters = () => {
        setSearchQuery('');
        setSelectedCategory('general');
    };

    // Sample FAQ data - in a real app, this might come from an API
    const faqItems = [
        // General Category
        {
            id: 'faq1',
            question: 'What are the FCC Broadband Labels and where can I find them?',
            answer: 'As of April 10th, 2024, the FCC requires all internet service providers like Xfinity, Spectrum, AT&T, and others to provide broadband labels for home internet service plans. These labels are intended to help consumers find the best plan for their needs by providing transparent information about pricing, speeds, and data caps.',
            hasReadMore: true,
            category: 'general',
            highlighted: false
        },
        {
            id: 'faq2',
            question: 'I want to work from home, which internet plan is best for me?',
            answer: 'For working from home, you,ll want a reliable connection with low latency and sufficient speeds for video conferencing. Fiber options from providers like AT&T, Ziplyfiber, Metronet, and Fidium typically offer the best performance. Cable providers like Xfinity, Spectrum, and Cox also provide solid options. If you,re in a rural area, satellite services from Hughesnet or Viasat might be your best choice. Enter your address on our site to see available providers in your area.',
            hasReadMore: false,
            category: 'general',
            highlighted: true
        },
        {
            id: 'faq3',
            question: 'What are the differences between cable, fiber, DSL, and satellite internet?',
            answer: 'Cable internet (from providers like Xfinity, Spectrum, and Cox) uses coaxial cables to deliver internet to your home. Fiber internet (AT&T Fiber, Ziplyfiber, Metronet) uses fiber-optic lines and typically offers the fastest speeds. DSL (AT&T, Kinetic, Earthlink) uses phone lines and is widely available but slower. Satellite internet (Hughesnet, Viasat) is available virtually anywhere with a clear view of the sky but may have higher latency.',
            hasReadMore: true,
            category: 'general',
            highlighted: false
        },
        {
            id: 'faq4',
            question: 'What are the top reasons to compare internet service providers?',
            answer: 'Comparing providers helps you find: Better Speeds: Plans with speeds up to 5 Gbps from fiber providers like AT&T and Metronet. Better Value: Find unlimited data plans without overage charges. Bundling Options: Save money by bundling internet with TV services like DIRECTV. Lower Latency: Experience enhanced performance with fiber or cable options. Availability: Find the best service available specifically in your location.',
            hasReadMore: true,
            category: 'general',
            highlighted: false
        },
        {
            id: 'faq5',
            question: 'What are the most common myths about internet service providers?',
            answer: 'Some common internet service myths include: Myth #1 – You can,t get fast internet in rural areas (providers like Kinetic, Hughesnet, and Viasat specialize in rural coverage). Myth #2 – The advertised speed is always what you,ll get (actual speeds may vary). Myth #3 – All unlimited plans are truly unlimited (some have throttling after certain usage). Myth #4 – You have to bundle services to get the best deal (standalone internet plans can often be very competitive).',
            hasReadMore: true,
            category: 'general',
            highlighted: false
        },
        {
            id: 'faq6', 
            question: 'Do internet plans come with built-in Wi-Fi?',
            answer: 'Most major providers like Xfinity, Spectrum, AT&T, and Cox include a Wi-Fi router or gateway with their internet plans. Many now feature advanced Wi-Fi 6 technology for better speed, enhanced security, and improved performance across multiple devices. Some providers charge a monthly equipment rental fee, while others include it in the plan price.',
            hasReadMore: false,
            category: 'general',
            highlighted: false
        },
        {
            id: 'faq7',
            question: 'How do I choose the right internet plan for my needs?',
            answer: 'Consider your online activities, number of connected devices, and budget. For basic browsing and email, plans from Spectrum, Xfinity, or AT&T with speeds of 25-100 Mbps may suffice. For streaming HD video or households with multiple users, look for plans with 100-300 Mbps from providers like Cox or Optimum. For 4K streaming, gaming, or working from home, consider high-speed plans of 300+ Mbps from fiber providers like AT&T Fiber, Ziplyfiber, or Metronet.',
            hasReadMore: true,
            category: 'general',
            highlighted: false
        },
        // Speed Category
        {
            id: 'faq8',
            question: 'How fast of an internet connection do I need?',
            answer: 'For basic web browsing and email: 25 Mbps. For HD video streaming: 25-50 Mbps. For multiple users streaming and gaming: 100-200 Mbps. For 4K streaming and downloading large files: 200+ Mbps. For households with many connected devices or work-from-home needs: 300+ Mbps. Heavy users with smart home devices and multiple 4K streams: 500+ Mbps to 1 Gig.',
            hasReadMore: true,
            category: 'speed',
            highlighted: false
        },
        {
            id: 'faq9',
            question: 'Which providers offer the fastest internet speeds?',
            answer: 'The fastest residential internet speeds are typically offered by fiber providers. AT&T Fiber, Ziplyfiber, and Metronet offer plans up to 5 Gbps in select areas. Xfinity, Spectrum, and Cox offer cable plans with speeds up to 1-2 Gbps. Actual availability varies by location, so enter your address on our site to see the fastest options in your area.',
            hasReadMore: false,
            category: 'speed',
            highlighted: false
        },
        {
            id: 'faq10',
            question: 'What is the difference between download and upload speeds?',
            answer: 'Download speed affects how quickly you can receive data (loading websites, streaming video, downloading files). Upload speed affects how quickly you can send data (video calls, uploading files, live streaming). Fiber providers like AT&T Fiber, Ziplyfiber, and Metronet typically offer symmetrical speeds (equal download and upload). Cable providers like Xfinity, Spectrum, and Cox usually have much faster download than upload speeds.',
            hasReadMore: true,
            category: 'speed',
            highlighted: false
        },
        // Data Category
        {
            id: 'faq11',
            question: 'Which providers offer truly unlimited data?',
            answer: 'Most fiber providers like AT&T Fiber, Ziplyfiber, and Metronet offer unlimited data with no caps. Among cable providers, Spectrum and WOW stand out for offering unlimited data on all plans. Xfinity, Cox, and others may have data caps on some plans (typically 1.2TB) with options to add unlimited data for an additional fee. Satellite providers like Hughesnet and Viasat typically offer unlimited data but may reduce speeds after reaching priority data thresholds.',
            hasReadMore: true,
            category: 'data',
            highlighted: false
        },
        {
            id: 'faq12',
            question: 'How much internet data does the average household use?',
            answer: 'The average household uses 500-600 GB of data per month, but usage varies greatly based on activities. HD video streaming uses about 3 GB per hour, while 4K streaming uses about 7 GB per hour. Video conferencing uses 0.5-2.5 GB per hour. Online gaming uses 50-150 MB per hour plus downloads. Most major providers offer plans with at least 1TB (1,000 GB) of data, which is sufficient for most households.',
            hasReadMore: true,
            category: 'data',
            highlighted: false
        },
        // Wi-Fi Category
        {
            id: 'faq13',
            question: 'What is Mesh Wi-Fi and which providers offer it?',
            answer: 'Mesh Wi-Fi uses multiple access points to eliminate dead zones and provide complete coverage throughout your home. Xfinity offers xFi Pods, AT&T offers Smart Wi-Fi Extenders, Spectrum offers Advanced Wi-Fi, and other providers have similar offerings. These mesh systems are particularly useful for larger homes or buildings with multiple floors or thick walls.',
            hasReadMore: true,
            category: 'wifi',
            highlighted: false
        },
        {
            id: 'faq14',
            question: 'Can I use my own router with my internet service?',
            answer: 'Most providers allow you to use your own compatible router. Xfinity, Spectrum, Cox, AT&T (for non-fiber services), and many others support customer-owned equipment. Using your own router can save on monthly rental fees, but you,ll be responsible for setup and maintenance. Some services, particularly fiber offerings like AT&T Fiber, may require the use of their gateway equipment but allow you to put it in passthrough mode to use your router,s features.',
            hasReadMore: true,
            category: 'wifi',
            highlighted: false
        },
        // Compatibility Category
        {
            id: 'faq15',
            question: 'Can I run a VPN with any internet service?',
            answer: 'Yes, VPNs work with all internet service providers including Xfinity, Spectrum, AT&T, Cox, and others. For optimal VPN performance, higher speeds are recommended. Fiber internet (AT&T Fiber, Ziplyfiber, Metronet) typically provides the best VPN experience due to lower latency and higher upload speeds. Cable providers (Xfinity, Spectrum, Cox) also work well with VPNs in most cases.',
            hasReadMore: false,
            category: 'compatibility',
            highlighted: false
        },
        {
            id: 'faq16',
            question: 'Will security cameras work with my internet service?',
            answer: 'Yes, security cameras work with all providers including Xfinity, Spectrum, AT&T, and others. For the best experience with multiple cameras, consider plans with higher upload speeds, particularly from fiber providers like AT&T Fiber, Ziplyfiber, or Metronet. If using wireless cameras, a strong Wi-Fi signal throughout your property is important. Some providers like Xfinity and Cox also offer their own security services that can be bundled with internet.',
            hasReadMore: true,
            category: 'compatibility',
            highlighted: false
        },
        // Gaming Category
        {
            id: 'faq17',
            question: 'Which internet service is best for online gaming?',
            answer: 'For online gaming, low latency (ping) is more important than raw speed. Fiber internet from providers like AT&T Fiber, Ziplyfiber, and Metronet typically offers the lowest latency. Cable providers like Xfinity, Spectrum, and Cox also perform well for gaming. Recommended speeds are at least 25-50 Mbps for most games, with 100+ Mbps for households with multiple gamers. Avoid satellite internet (Hughesnet, Viasat) for competitive gaming due to higher latency.',
            hasReadMore: true,
            category: 'gaming',
            highlighted: false
        },
        {
            id: 'faq18',
            question: 'What is ping/latency and why does it matter for gaming?',
            answer: 'Ping or latency is the time it takes for data to travel between your device and the game server, measured in milliseconds (ms). Lower is better for gaming. Fiber providers (AT&T Fiber, Ziplyfiber, Metronet) typically offer the lowest latency (10-20ms). Cable providers (Xfinity, Spectrum, Cox) usually have good latency (15-40ms). DSL (AT&T, Kinetic) can vary widely (20-80ms). Satellite providers (Hughesnet, Viasat) have significantly higher latency (500+ ms) due to the distance signals must travel.',
            hasReadMore: true,
            category: 'gaming',
            highlighted: false
        },
        // Streaming Category
        {
            id: 'faq19',
            question: 'Which internet speeds do I need for streaming?',
            answer: 'For SD quality: 3-5 Mbps. For HD quality: 5-10 Mbps per stream. For 4K streaming: 25-35 Mbps per stream. For households with multiple simultaneous streams, add up the requirements. Providers like Xfinity, Spectrum, AT&T, and Cox all offer plans suitable for streaming. For multiple 4K streams, consider plans of 200+ Mbps from providers like AT&T Fiber, Xfinity, or Metronet.',
            hasReadMore: true,
            category: 'streaming',
            highlighted: false
        },
        {
            id: 'faq20',
            question: 'Which providers offer bundles with streaming services?',
            answer: 'Several providers offer internet bundles with streaming services. Xfinity offers plans that include Peacock Premium. AT&T may include HBO Max with certain plans. DIRECTV Stream bundles include streaming TV options. Spectrum offers mobile plans that can be bundled with internet. These offerings change frequently, so check each provider,s current promotions when comparing options.',
            hasReadMore: false,
            category: 'streaming',
            highlighted: false
        },
        // Voice/Phone Category
        {
            id: 'faq21',
            question: 'Can I get home phone service with my internet?',
            answer: 'Yes, most major providers including Xfinity, Spectrum, AT&T, and Cox offer home phone service that can be bundled with internet. These services typically use Voice over IP (VoIP) technology and include features like caller ID, call waiting, and voicemail. Bundling phone service with internet often provides better value than purchasing separately.',
            hasReadMore: false,
            category: 'voice',
            highlighted: false
        },
        {
            id: 'faq22',
            question: 'Can I keep my current phone number if I switch providers?',
            answer: 'Yes, federal regulations allow you to transfer (or "port") your existing phone number when switching providers. This applies to all major providers including Xfinity, Spectrum, AT&T, Cox, and others. To ensure a smooth transfer, don,t cancel your current service before the new service is activated. Your new provider will handle the transfer process, which typically takes 1-2 business days.',
            hasReadMore: true,
            category: 'voice',
            highlighted: false
        },
        // Installation Category
        {
            id: 'faq23',
            question: 'Do all providers charge installation fees?',
            answer: 'Installation fees vary by provider. Some providers like Xfinity and Spectrum frequently offer promotions with free professional installation. Others may charge $50-$100 for professional installation. Many providers like AT&T, Cox, and Optimum offer self-installation options at reduced or no cost. Installation fees are often negotiable or waived with certain promotions or when signing up for higher-tier plans.',
            hasReadMore: false,
            category: 'installation',
            highlighted: false
        },
        {
            id: 'faq24',
            question: 'How long does internet installation typically take?',
            answer: 'Professional installation typically takes 1-2 hours for cable providers like Xfinity, Spectrum, and Cox. Fiber installation from providers like AT&T Fiber, Ziplyfiber, or Metronet may take 2-4 hours, especially if it,s the first fiber installation at your address. Self-installation usually takes 30-60 minutes once you have the equipment. Satellite installation (Hughesnet, Viasat) typically takes 2-3 hours as it requires precise dish alignment.',
            hasReadMore: true,
            category: 'installation',
            highlighted: false
        },
        // Pricing Category
        {
            id: 'faq25',
            question: 'Why do internet prices increase after the promotional period?',
            answer: 'Most providers including Xfinity, Spectrum, AT&T, and Cox offer introductory pricing for new customers, typically for 12-24 months. After this period, pricing increases to the standard rate. This is a common industry practice. To get the best rates, you can often negotiate with your provider when the promotional period ends or explore switching to another provider with new customer promotions.',
            hasReadMore: true,
            category: 'pricing',
            highlighted: false
        },
        {
            id: 'faq26',
            question: 'Are there government subsidies available for internet service?',
            answer: 'Yes, the Affordable Connectivity Program (ACP) provides eligible households with discounts on internet service (up to $30/month, or $75/month on Tribal lands). Most major providers participate, including Xfinity, Spectrum, AT&T, Cox, and others. Some providers also offer their own low-income internet programs, such as Xfinity,s Internet Essentials, AT&T Access, and Spectrum Internet Assist.',
            hasReadMore: true,
            category: 'pricing',
            highlighted: false
        },
        // Contracts Category
        {
            id: 'faq27',
            question: 'Which providers offer no-contract internet plans?',
            answer: 'Several providers offer no-contract options including Spectrum, WOW, Metronet, and Ziplyfiber on all their plans. Others like Xfinity, AT&T, and Cox offer both contract and no-contract options, though no-contract plans may cost slightly more. No-contract plans provide flexibility but may have higher upfront costs or miss out on promotional pricing that requires a term agreement.',
            hasReadMore: true,
            category: 'contracts',
            highlighted: false
        },
        {
            id: 'faq28',
            question: 'What are early termination fees if I break my contract?',
            answer: 'Early termination fees vary by provider. AT&T typically charges up to $180, decreasing by $15 per month of service completed. Xfinity,s fees can be up to $230, prorated based on remaining contract months. Cox charges up to $120 for early termination. Hughesnet and Viasat may charge higher early termination fees for their satellite services. Providers like Spectrum, WOW, and Ziplyfiber don,t require contracts, so there are no early termination fees.',
            hasReadMore: true,
            category: 'contracts',
            highlighted: false
        },
        // Rural Internet Category
        {
            id: 'faq29',
            question: 'What are my internet options in rural areas?',
            answer: 'Rural internet options include: Satellite internet from Hughesnet and Viasat (available virtually everywhere with a clear view of the sky). DSL from providers like AT&T, Kinetic by Windstream, and Earthlink where available. Fixed wireless from providers like AT&T and local WISPs. Some rural areas may have access to cable internet from providers like Spectrum and Xfinity or even fiber from Ziplyfiber, Metronet, or AT&T Fiber in select locations.',
            hasReadMore: true,
            category: 'rural',
            highlighted: false
        },
        {
            id: 'faq30',
            question: 'How does satellite internet compare to other options?',
            answer: 'Satellite internet from providers like Hughesnet and Viasat offers widespread availability but has higher latency (delay) than wired options. Speeds typically range from 25-100 Mbps download. Data allowances may be limited, with reduced speeds after reaching monthly thresholds. Weather can occasionally affect reliability. For rural areas without cable or fiber options, satellite internet provides essential connectivity, though at generally higher costs than urban internet services.',
            hasReadMore: true,
            category: 'rural',
            highlighted: false
        },
    ];
    return (
        <>
            <Header />
            <div className="fq-header">
                <h1>Frequently Asked Questions</h1>
                <div className="fq-search-container">
                    <div className="fq-search-info">Enter keywords. Example: Satellite Internet</div>
                    <input
                        type="text"
                        className="fq-search"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="fq-search-btn">🔍</button>
                </div>
            </div>

            <div className="fq-container">
                <div className="fq-sidebar">
                    <h2 className="fq-category-title">Categories</h2>
                    <ul className="fq-category-list">
                        <li className="fq-category-item">
                            <input
                                type="radio"
                                name="category"
                                id="general"
                                className="fq-category-radio"
                                checked={selectedCategory === 'general'}
                                onChange={() => setSelectedCategory('general')}
                            />
                            <label htmlFor="general" className="fq-category-label">General <span className="fq-category-count">(19)</span></label>
                        </li>
                        <li className="fq-category-item">
                            <input
                                type="radio"
                                name="category"
                                id="fusion"
                                className="fq-category-radio"
                                checked={selectedCategory === 'fusion'}
                                onChange={() => setSelectedCategory('fusion')}
                            />
                            <label htmlFor="fusion" className="fq-category-label">Fusion <span className="fq-category-count">(5)</span></label>
                        </li>
                        {/* Add other categories similarly */}
                        {/* ... */}
                        <li className="fq-category-item">
                            <input
                                type="radio"
                                name="category"
                                id="norton"
                                className="fq-category-radio"
                                checked={selectedCategory === 'norton'}
                                onChange={() => setSelectedCategory('norton')}
                            />
                            <label htmlFor="norton" className="fq-category-label">Norton <span className="fq-category-count">(7)</span></label>
                        </li>
                    </ul>
                    <button className="fq-clear-btn" onClick={clearFilters}>Clear Filters</button>
                </div>

                <div className="fq-content">
                    <h2 className="fq-section-title">General</h2>

                    {/* Example of how to render FAQ items with React */}
                    {faqItems.map((item) => (
                        <div
                            key={item.id}
                            className={`fq-item ${item.highlighted ? 'fq-highlighted' : ''}`}
                        >
                            <div className="fq-question" onClick={() => toggleExpand(item.id)}>
                                <p className="fq-question-text"><span>Q:</span> {item.question}</p>
                                <button className="fq-expand-btn">
                                    {expandedItems[item.id] ? '-' : '+'}
                                </button>
                            </div>
                            <div
                                className="fq-answer"
                                style={{ display: expandedItems[item.id] ? 'block' : 'none' }}
                            >
                                <p><span>A:</span> {item.answer}
                                    {item.hasReadMore && <a href="#" className="fq-read-more"> Read More</a>}
                                </p>
                            </div>
                        </div>
                    ))}

                    {/* You can convert the rest of your static FAQ items to use the mapping approach above */}
                </div>
            </div>
            <Footer />
        </>
    );
}

export default FAQ;